import { Box, FormControl, MenuItem, Paper, Select, Typography } from '@mui/material';

import { useWallStore, useWorkspaceUiStore } from '@walls/store';
import { getPanelColor, getPanelTitleColor } from '../theme/wallThemeStyles';
import { ColorControl } from './ShapeToolDialog';

const fontOptions = [
  { label: 'Arial', value: 'Arial,Arial,Helvetica,sans-serif' },
  { label: 'Arial Black', value: 'Arial Black,Arial Black,Gadget,sans-serif' },
  { label: 'Comic Sans MS', value: 'Comic Sans MS,Comic Sans MS,cursive' },
  { label: 'Courier New', value: 'Courier New,Courier New,Courier,monospace' },
  { label: 'Georgia', value: 'Georgia,Georgia,serif' },
  { label: 'Impact', value: 'Impact,Charcoal,sans-serif' },
  { label: 'Lucida Console', value: 'Lucida Console,Monaco,monospace' },
  { label: 'Lucida Sans Unicode', value: 'Lucida Sans Unicode,Lucida Grande,sans-serif' },
  { label: 'Palatino Linotype', value: 'Palatino Linotype,Book Antiqua,Palatino,serif' },
  { label: 'Tahoma', value: 'Tahoma,Geneva,sans-serif' },
  { label: 'Times New Roman', value: 'Times New Roman,Times,serif' },
  { label: 'Trebuchet MS', value: 'Trebuchet MS,Helvetica,sans-serif' },
  { label: 'Verdana', value: 'Verdana,Geneva,sans-serif' },
  { label: 'Gill Sans', value: 'Gill Sans,Geneva,sans-serif' },
];

const fontSizes = [18, 20, 22, 24, 26, 28, 36, 48, 72];

interface TextToolDialogProps {
  layoutStyle?: string;
}

export function TextToolDialog({ layoutStyle }: TextToolDialogProps) {
  const selectedFontColor = useWorkspaceUiStore((state) => state.selectedFontColor);
  const selectedFontSize = useWorkspaceUiStore((state) => state.selectedFontSize);
  const selectedFontStyle = useWorkspaceUiStore((state) => state.selectedFontStyle);
  const setSelectedFontColor = useWorkspaceUiStore((state) => state.setSelectedFontColor);
  const setSelectedFontSize = useWorkspaceUiStore((state) => state.setSelectedFontSize);
  const setSelectedFontStyle = useWorkspaceUiStore((state) => state.setSelectedFontStyle);
  const theme = useWallStore((state) => state.theme);

  return (
    <Paper
      elevation={0}
      onClick={(event) => event.stopPropagation()}
      sx={{
        bgcolor: getPanelColor(theme),
        color: getPanelTitleColor(theme),
        boxShadow: '0px 3px 6px #00000029',
        bottom: {
          xs: layoutStyle === 'open' ? 'auto' : 10,
          sm: 'auto',
        },
        left: { xs: 68, sm: 78 },
        maxHeight: 'calc(100vh - 20px)',
        overflowY: 'auto',
        position: 'fixed',
        top: {
          xs: layoutStyle === 'open' ? 70 : 'auto',
          sm: '50%',
        },
        transform: { xs: 'none', sm: 'translateY(-50%)' },
        width: { xs: 'min(235px, calc(100vw - 78px))', sm: 235 },
        zIndex: 1001,
      }}
    >
      <Box sx={{ display: 'grid', gap: 1.25, p: 1.25 }}>
        <Box sx={{ display: 'grid', gap: 0.75, gridTemplateColumns: '1fr 70px' }}>
          <Box>
            <Typography sx={sectionTitleSx}>Font</Typography>
            <FormControl fullWidth size="small">
              <Select
                aria-label="Font family"
                value={selectedFontStyle}
                onChange={(event) => setSelectedFontStyle(event.target.value)}
                sx={{ fontSize: 14 }}
              >
                {fontOptions.map((font) => (
                  <MenuItem key={font.value} value={font.value} sx={{ fontFamily: font.value }}>
                    {font.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography sx={sectionTitleSx}>Size</Typography>
            <FormControl fullWidth size="small">
              <Select
                aria-label="Font size"
                value={selectedFontSize}
                onChange={(event) => setSelectedFontSize(Number(event.target.value))}
                sx={{ fontSize: 14 }}
              >
                {fontSizes.map((fontSize) => (
                  <MenuItem key={fontSize} value={fontSize}>
                    {fontSize}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <ColorControl
          label="Text color"
          previewColor={selectedFontColor}
          value={selectedFontColor}
          onChange={setSelectedFontColor}
        />
      </Box>
    </Paper>
  );
}

const sectionTitleSx = {
  fontSize: 13,
  fontWeight: 700,
  mb: 0.625,
  textAlign: 'left',
};
